/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AdminAssetGroup } from '../types'

const log = logger({ category: 'useAssetGroups' })

export interface AssetGroupOption {
  /** Fresh company id — what a profile stores. */
  id: number
  name: string
}

export interface AssetGroupsState {
  groups: AssetGroupOption[]
  isLoading: boolean
  error: string
}

/** The asset groups (sub-companies) of one customer, for the scope picker. Nothing is fetched without a customer. */
export const useAssetGroups = (customerId: number | undefined): AssetGroupsState => {
  const { api } = useApi()
  const [groups, setGroups] = useState<AssetGroupOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (customerId === undefined) {
      setGroups([])
      setError('')
      return
    }
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminAssetGroup[]>({
          path: `/app/admin/asset-groups?customerId=${encodeURIComponent(customerId)}`,
          origin: 'useAssetGroups',
        })
        if (cancelled) return
        if (response.error) {
          setError(response.message || 'Could not load asset groups')
        } else {
          setError('')
          setGroups((response.data ?? []).map((g) => ({ id: g.companyId, name: g.name })))
        }
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load asset groups')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, customerId])

  return { groups, isLoading, error }
}
